import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import api from '../../services/api';
import { topLayerTokens } from '../../styles/topLayerTokens';

// ─── Global panel styles injected once per mounted MegaMenu instance ───────────
const PANEL_STYLES = `
  .mega-menu-panel h3 {
    font-size: 13px;
    font-weight: 700;
    color: #1a5d3a;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-top: 0;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #f5a623;
  }
  .mega-menu-panel a {
    font-size: 15px;
    color: #2d2d2d;
    line-height: 2;
    font-weight: 400;
    display: block;
    text-decoration: none;
    transition: color 0.15s ease;
  }
  .mega-menu-panel a:hover {
    color: #1a5d3a;
  }
  /* Col 1 sidebar — targets bg-gray-50 divs (Magazine + Guidance sidebars) */
  .mega-menu-panel .bg-gray-50 {
    background: #f5f0e8 !important;
    border-radius: 8px;
    padding: 16px;
  }
  /* Global CTA button — TASK 2 */
  .mega-menu-btn {
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    display: block;
    text-align: center;
    text-decoration: none;
    line-height: 1.5;
    border-radius: 6px;
    cursor: pointer;
  }
`;

const MegaMenu = ({
  label,
  menuKey,
  children,
  className = '',
  topPosition = 104,
  staticFeaturedCard = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [feature, setFeature] = useState(null);
  const closeTimeoutRef = React.useRef(null);

  useEffect(() => {
    if (menuKey) {
      fetchFeature();
    }
  // eslint-disable-next-line
  }, [menuKey]);

  const fetchFeature = async () => {
    try {
      const response = await api.get(`/api/mega-menu/features/${menuKey}`);
      setFeature(response.data.feature);
    } catch (error) {
      console.error('Failed to load feature:', error);
    }
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Guidance: strip card box from featured panel; all others keep it
  const hidePanelCard = ['guidance', 'tools', 'ask_oz', 'signal_media', 'media_hub'].includes(menuKey);
  // Media Hub spans all 4 columns equally (no separate featured rail)
  const fullWidth = menuKey === 'media_hub';

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Inject panel styles whenever any MegaMenu is mounted */}
      <style>{PANEL_STYLES}</style>

      <button
        className={`text-sm font-medium text-gray-700 hover:text-[#1a5d3a] transition-colors ${className}`}
      >
        {label}
      </button>

      {isOpen && (
        <div
          className="mega-menu-panel fixed left-1/2 -translate-x-1/2 bg-white border-x border-b border-gray-100 rounded-b-xl shadow-2xl"
          style={{
            top: `${topPosition}px`,
            zIndex: 45,
            width: '1100px',
            height: '480px',
            maxWidth: 'calc(100vw - 32px)',
            padding: '40px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* ── Content grid ── */}
          <div
            className="grid grid-cols-4 gap-8"
            style={{ flex: 1, minHeight: 0 }}
          >
            <div className={fullWidth ? 'col-span-4' : 'col-span-3'} style={{ overflow: 'hidden' }}>
              {children}
            </div>

            {/* ── Featured / right panel ── */}
            {!fullWidth && (
            <div
              className={hidePanelCard
                ? 'flex flex-col'
                : `${topLayerTokens.radius.panel} p-5 border-2 border-gray-100 hover:border-[#1a5d3a]/20 ${topLayerTokens.animation.timing} ${topLayerTokens.animation.duration} flex flex-col`}
              style={hidePanelCard ? {} : { background: 'white' }}
            >
              {staticFeaturedCard ? staticFeaturedCard : feature ? (
                <>
                  {feature.image_url && (
                    <div className="relative mb-3">
                      <img
                        src={feature.image_url}
                        alt={feature.title}
                        className={`w-full h-32 object-cover ${topLayerTokens.radius.button}`}
                      />
                      <span className="absolute top-2 right-2 px-2 py-1 bg-[#1a5d3a] text-white text-xs font-bold rounded shadow-md">
                        {feature.label}
                      </span>
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <Link
                    to={feature.cta_url}
                    className="inline-flex items-center gap-2 text-[#1a5d3a] font-semibold text-sm hover:gap-3 transition-all mt-auto"
                  >
                    {feature.cta_text}
                    <ArrowRight size={16} />
                  </Link>
                </>
              ) : (
                <div className="flex flex-col justify-center items-center h-full text-center">
                  <div className="text-4xl mb-3">🎯</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Featured</h3>
                  <p className="text-sm text-gray-600">More coming soon</p>
                </div>
              )}
            </div>
            )}
          </div>

          {/* ── Bottom bar ── */}
          {!fullWidth && (
          <div
            style={{
              width: '100%',
              borderTop: '1px solid #f5a623',
              marginTop: 'auto',
              paddingTop: '16px',
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                fontSize: '13px',
                color: '#f5a623',
                fontWeight: 600,
              }}
            >
              Powered by Ask Oz AI
            </span>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export const MegaMenuSection = ({ title, links }) => (
  <div>
    <h3>{title}</h3>
    <div>
      {links.map((link, index) => (
        <Link key={index} to={link.href}>
          {link.label}
        </Link>
      ))}
    </div>
  </div>
);

export default MegaMenu;
